/**
 * Alert component overrides.
 * Glass-like alerts with rounded icon containers. CoOrdio DS icons (Lucide).
 */

import type { Theme } from '@mui/material/styles';
import { AlertCircle, AlertTriangle, Info, CheckCircle } from '@core/components/icons';

const alerts: Theme['components'] = {
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        error: <AlertCircle size={24} />,
        warning: <AlertTriangle size={24} />,
        info: <Info size={24} />,
        success: <CheckCircle size={24} />,
      },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(3.5, 4),
        gap: theme.spacing(4),
        borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
        border: '1px solid var(--glass-border)',
        ...theme.typography.body1,
        '&:not(:has(.MuiAlertTitle-root))': {
          '& .MuiAlert-icon + .MuiAlert-message': {
            alignSelf: 'center',
          },
        },
        variants: [
          {
            props: { variant: 'standard', severity: 'error' },
            style: {
              '& .MuiAlert-icon': {
                backgroundColor: 'var(--mui-palette-error-main)',
                color: 'var(--mui-palette-error-contrastText)',
              },
            },
          },
          {
            props: { variant: 'standard', severity: 'warning' },
            style: {
              '& .MuiAlert-icon': {
                backgroundColor: 'var(--mui-palette-warning-main)',
                color: 'var(--mui-palette-warning-contrastText)',
              },
            },
          },
          {
            props: { variant: 'standard', severity: 'info' },
            style: {
              '& .MuiAlert-icon': {
                backgroundColor: 'var(--mui-palette-info-main)',
                color: 'var(--mui-palette-info-contrastText)',
              },
            },
          },
          {
            props: { variant: 'standard', severity: 'success' },
            style: {
              '& .MuiAlert-icon': {
                backgroundColor: 'var(--mui-palette-success-main)',
                color: 'var(--mui-palette-success-contrastText)',
              },
            },
          },
        ],
      }),
      icon: {
        padding: 0,
        margin: 0,
        minInlineSize: 32,
        blockSize: 32,
        borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        alignItems: 'center',
        justifyContent: 'center',
        '& i, & svg': {
          fontSize: 'inherit',
        },
      },
      message: {
        padding: 0,
      },
      action: {
        padding: 0,
        marginRight: 0,
      },
    },
  },
  MuiAlertTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: theme.typography.h5.fontSize,
        fontWeight: 600,
        lineHeight: 1.33333,
        marginTop: 0,
        marginBottom: theme.spacing(1),
        color: 'inherit',
      }),
    },
  },
};

export default alerts;
